import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LangContext";
import { LANGS } from "@/lib/i18n";
import { Activity, LayoutDashboard, Radar, Wallet, Settings as Cog, LogOut, Globe, Bot, BarChart3 } from "lucide-react";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { lang, setLang, t } = useLang();
  const loc = useLocation();
  const nav = useNavigate();
  const [langOpen, setLangOpen] = React.useState(false);

  const link = (to, label, Icon, testid) => {
    const active = loc.pathname === to || (to !== "/" && loc.pathname.startsWith(to));
    return (
      <Link
        to={to}
        data-testid={testid}
        className={`flex items-center gap-2 px-4 py-2 text-xs font-display uppercase tracking-wider transition-colors ${
          active ? "text-[#CCFF00] border-b-2 border-[#CCFF00]" : "text-white/70 hover:text-white"
        }`}
      >
        {Icon && <Icon size={14} strokeWidth={2.5} />} {label}
      </Link>
    );
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Top bar */}
      <header className="sticky top-0 z-50 backdrop-blur-2xl bg-black/70 border-b border-[#222]">
        <div className="max-w-[1400px] mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="nav-home-logo" className="flex items-center gap-3">
            <div className="w-8 h-8 bg-[#CCFF00] flex items-center justify-center">
              <Activity size={18} className="text-black" strokeWidth={3} />
            </div>
            <span className="font-display text-lg font-bold tracking-tight">X1K<span className="text-[#CCFF00]">.</span>AI</span>
          </Link>

          {user && (
            <nav className="hidden md:flex items-center gap-1">
              {link("/dashboard", t("nav_dashboard") || "Dashboard", LayoutDashboard, "nav-dashboard-link")}
              {link("/autotrade", t("nav_autotrade") || "Auto-Trade", Bot, "nav-autotrade-link")}
              {link("/scanner", t("nav_scanner") || "AI Scanner", Radar, "nav-scanner-link")}
              {link("/performance", t("nav_performance") || "Performance", BarChart3, "nav-performance-link")}
              {link("/programs", t("nav_programs") || "Programs", Wallet, "nav-programs-link")}
              {link("/settings", t("nav_settings") || "Settings", Cog, "nav-settings-link")}
            </nav>
          )}
          {!user && (
            <nav className="flex items-center gap-1">
              {link("/performance", t("nav_performance") || "Performance", BarChart3, "nav-performance-link")}
            </nav>
          )}

          <div className="flex items-center gap-3">
            {/* Lang switcher */}
            <div className="relative">
              <button
                onClick={() => setLangOpen((v) => !v)}
                data-testid="lang-switcher-button"
                className="flex items-center gap-2 px-3 py-2 border border-[#222] text-xs font-mono hover:border-[#CCFF00] transition-colors"
              >
                <Globe size={14} /> {lang.toUpperCase()}
              </button>
              {langOpen && (
                <div className="absolute right-0 top-full mt-1 bg-[#0A0A0A] border border-[#222] min-w-[180px] max-h-[300px] overflow-y-auto z-50">
                  {LANGS.map((l) => (
                    <button
                      key={l.code}
                      onClick={() => { setLang(l.code); setLangOpen(false); }}
                      data-testid={`lang-option-${l.code}`}
                      className={`w-full text-left px-4 py-2 text-xs font-mono hover:bg-[#141414] flex items-center justify-between ${
                        lang === l.code ? "text-[#CCFF00]" : "text-white"
                      }`}
                    >
                      <span>{l.label}</span><span className="text-white/40">{l.flag}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {user ? (
              <button
                onClick={() => { logout(); nav("/"); }}
                data-testid="nav-logout-button"
                className="btn-ghost !py-2 !px-3 !text-xs flex items-center gap-2"
              >
                <LogOut size={14} /> {t("nav_logout") || "Logout"}
              </button>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login-link" className="btn-ghost !py-2 !px-3 !text-xs">{t("nav_login") || "Login"}</Link>
                <Link to="/register" data-testid="nav-register-link" className="btn-acid !py-2 !px-3 !text-xs">{t("nav_register") || "Get Started"}</Link>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="flex-1 pb-20 md:pb-0">{children}</main>

      {/* Mobile bottom nav (PWA) */}
      {user && (
        <nav data-testid="mobile-bottom-nav" className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-xl border-t border-[#222] grid grid-cols-5" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
          {[
            { to: "/dashboard", label: "Dash", Icon: LayoutDashboard, tid: "mobile-nav-dashboard" },
            { to: "/autotrade", label: "Auto", Icon: Bot, tid: "mobile-nav-autotrade" },
            { to: "/scanner", label: "Scan", Icon: Radar, tid: "mobile-nav-scanner" },
            { to: "/programs", label: "Progs", Icon: Wallet, tid: "mobile-nav-programs" },
            { to: "/settings", label: "Config", Icon: Cog, tid: "mobile-nav-settings" },
          ].map(({ to, label, Icon, tid }) => {
            const active = loc.pathname.startsWith(to);
            return (
              <Link key={to} to={to} data-testid={tid}
                className={`flex flex-col items-center justify-center gap-1 py-2.5 text-[9px] font-mono uppercase tracking-wide ${active ? "text-[#CCFF00]" : "text-white/50"}`}>
                <Icon size={18} strokeWidth={active ? 2.5 : 2} />
                <span className="truncate max-w-[64px]">{label}</span>
              </Link>
            );
          })}
        </nav>
      )}

      <footer className="border-t border-[#222] mt-20">
        <div className="max-w-[1400px] mx-auto px-6 py-8 flex flex-col md:flex-row md:items-center justify-between gap-4 text-xs text-white/40 font-mono">
          <div>X1K.AI © 2026 — {t("risk_title") || "RISK DISCLOSURE"}: Crypto trading carries extreme financial risk. You may lose everything.</div>
          <div>Powered by Claude 4.5 · DexScreener · PumpFun · Stripe</div>
        </div>
      </footer>
    </div>
  );
}
